/**
 * Modal de copie de lien vers une donne différé (replay).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { DiffereNavigationRequest } from '../types/app';
import { CloseIcon, LinkIcon } from './Icons';
import { buildDiffereShareUrl } from '../utils/differeDeepLink';
import '../styles/library.css';

interface DiffereShareLinkModalProps {
  request: DiffereNavigationRequest;
  playerLabel?: string;
  dealLabel?: string;
  onClose: () => void;
}

export default function DiffereShareLinkModal({
  request,
  playerLabel,
  dealLabel,
  onClose,
}: DiffereShareLinkModalProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [copied, setCopied] = useState(false);
  const url = buildDiffereShareUrl(request);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      inputRef.current?.select();
    }
  }, [url]);

  const contextLine = [
    playerLabel,
    dealLabel ? `donne ${dealLabel}` : request.dealNum != null ? `donne ${request.dealNum}` : null,
  ].filter(Boolean).join(' · ');

  return (
    <div className="library-modal-overlay" onClick={onClose}>
      <div className="library-modal library-share-link-modal" onClick={e => e.stopPropagation()}>
        <div className="library-modal-header">
          <h3>Partager cette donne</h3>
          <button type="button" className="library-modal-close" onClick={onClose} aria-label="Fermer">
            <CloseIcon size={16} />
          </button>
        </div>

        <div className="library-modal-body">
          <p className="library-share-link-hint">
            {contextLine
              ? `Lien direct vers le replay différé : ${contextLine}.`
              : 'Lien direct vers le replay de cette donne différé.'}
            {' '}Le destinataire doit avoir accès à l&apos;espace compétition.
          </p>

          <label className="library-modal-label" htmlFor="differe-share-link-input">
            Lien direct
          </label>
          <div className="library-share-link-row">
            <input
              id="differe-share-link-input"
              ref={inputRef}
              className="library-share-link-input"
              readOnly
              value={url}
              onFocus={e => e.target.select()}
            />
            <button
              type="button"
              className={`library-modal-btn primary library-share-link-copy${copied ? ' copied' : ''}`}
              onClick={() => { void handleCopy(); }}
            >
              <LinkIcon size={14} />
              {copied ? 'Copié ✓' : 'Copier'}
            </button>
          </div>
        </div>

        <div className="library-modal-footer">
          <div className="library-modal-buttons">
            <button type="button" className="library-modal-btn secondary" onClick={onClose}>
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
